import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import { fetchData } from "../../../utils/ApiCall";
import { sortAlphabetically } from "../../../json/CampaignCreator";

const theme = createTheme({
  palette: {
    primary: {
      main: "#0971f1",
      darker: "#053e85",
    },
  },
  typography: {
    fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
  },
});

const TrackingAndTargeting = ({
  dataForm,
  setDataForm,
  apiUrl,
  isEdit,
  personalTargeting,
  transformedPersonalTargeting,
  setPersonalTargeting,
  setTransformedPersonalTargeting,
}) => {
  const [countriesTargeting, setCountriesTargeting] = useState([]);
  const [countriesTracking, setCountriesTracking] = useState([]);
  const [affiliates, setAffiliates] = useState([]);

  useEffect(() => {
    const fetchApi = async () => {
      const resCountries = await fetchData(
        `${apiUrl}offer-service/getCountries`
      );
      setCountriesTargeting(sortAlphabetically(resCountries, "title"));
      setCountriesTracking(sortAlphabetically(resCountries, "title"));

      const resAffiliates = await fetchData(
        `${apiUrl}affiliate/getAllAffiliates`
      );
      setAffiliates(resAffiliates);
    };

    fetchApi();
  }, [apiUrl]);

  const handleSetFieldInPersonalTargeting = (index, fieldName, value) => {
    const updatedPersonalTargeting = [...personalTargeting];
    updatedPersonalTargeting[index][fieldName] = value;
    setPersonalTargeting(updatedPersonalTargeting);
    setTransformedPersonalTargeting(updatedPersonalTargeting);
  };

  const handleAddTracking = () => {
    // Add a condition check before allowing new personal targeting items
    if (dataForm.previewUrl && dataForm.countriesTargeting?.length > 0) {
      setPersonalTargeting([
        ...personalTargeting,
        { urls: [], affiliates: [], country: [] },
      ]);
    }
  };

  const handleChangeDataForm = (e) => {
    const { name, value } = e.target;
    setDataForm((prevState) => ({ ...prevState, [name]: value }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        width: "800px",
      }}
    >
      <div style={{ width: "100%" }}>
        {isEdit && (
          <p style={{ marginBottom: "20px", fontWeight: "bold" }}>
            General Targeting
          </p>
        )}

        <div style={{ width: "100%", padding: isEdit ? "20px" : "0px" }}>
          <div style={{ marginBottom: "20px" }}>
            <TextField
              id="preview-url"
              label="Preview URL"
              variant="standard"
              sx={{ width: "100%" }}
              name="previewUrl"
              onChange={handleChangeDataForm}
              value={dataForm.previewUrl}
            />
          </div>

          <div>
            <Autocomplete
              size="small"
              multiple
              disablePortal
              id="combo-box-countries-targeting"
              options={countriesTargeting}
              getOptionLabel={(countries) => countries?.name}
              sx={{ width: "100%" }}
              value={
                dataForm.countriesTargeting !== null
                  ? dataForm?.countriesTargeting?.map((country) =>
                      countriesTargeting.find((c) => c.code === country)
                    )
                  : []
              }
              onChange={(event, newValue) =>
                setDataForm((prevDataForm) => ({
                  ...prevDataForm,
                  countriesTargeting: newValue.map((item) => item.code),
                }))
              }
              renderOption={(props, option) => (
                <Box
                  component="li"
                  sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                  {...props}
                >
                  <img
                    loading="lazy"
                    width="20"
                    src={`https://flagcdn.com/w20/${option?.code?.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${option?.code?.toLowerCase()}.png 2x`}
                    alt=""
                  />
                  {option.label} {option.name}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Countries"
                  variant="standard"
                  value={dataForm.countriesTargeting}
                />
              )}
            />
          </div>
        </div>
      </div>

      {isEdit && (
        <div style={{ width: "100%" }}>
          <Divider sx={{ margin: "2em 0px " }} />
          <p style={{ marginBottom: "20px", fontWeight: "bold" }}>
            Personal Targeting
          </p>
          {personalTargeting?.map((tracking, index) => (
            <div
              key={index}
              style={{
                backgroundColor: "var(--blueLow)",
                margin: "20px 0",
                padding: "20px",
              }}
            >
              <div style={{ marginBottom: "20px", display: "flex" }}>
                <Autocomplete
                  size="small"
                  multiple
                  disablePortal
                  id={`affiliates-select-${index}`}
                  options={affiliates}
                  getOptionLabel={(aff) => aff?.title}
                  sx={{ width: "385px", marginRight: "30px" }}
                  value={tracking.affiliates.map((affiliateId) =>
                    affiliates.find((aff) => aff.affiseID === affiliateId)
                  )}
                  onChange={(event, newValue) =>
                    handleSetFieldInPersonalTargeting(
                      index,
                      "affiliates",
                      newValue.map((aff) => aff.affiseID)
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Affiliates"
                      variant="standard"
                    />
                  )}
                />
                <Autocomplete
                  size="small"
                  multiple
                  disablePortal
                  id={`country-select-${index}`}
                  options={countriesTracking}
                  getOptionLabel={(option) => option?.name}
                  sx={{ width: "385px" }}
                  value={tracking.country.map((countryCode) =>
                    countriesTargeting.find((c) => c.code === countryCode)
                  )}
                  onChange={(event, newValue) =>
                    handleSetFieldInPersonalTargeting(
                      index,
                      "country",
                      newValue.map((c) => c.code)
                    )
                  }
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                      {...props}
                    >
                      <img
                        loading="lazy"
                        width="20"
                        src={`https://flagcdn.com/w20/${option?.code?.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w40/${option?.code?.toLowerCase()}.png 2x`}
                        alt=""
                      />
                      {option.label} {option.name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Countries"
                      variant="standard"
                    />
                  )}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                {tracking?.urls?.map((url, urlIndex) => (
                  <div key={urlIndex} style={{ marginBottom: "20px" }}>
                    <TextField
                      id={`url-${index}-${urlIndex}`}
                      label={`URL ${urlIndex + 1}`}
                      variant="standard"
                      sx={{ width: "760px" }}
                      value={url}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const updatedPersonalTargeting = [...personalTargeting];
                        updatedPersonalTargeting[index].urls[urlIndex] =
                          newValue;
                        setPersonalTargeting(updatedPersonalTargeting);
                      }}
                    />
                  </div>
                ))}
              </div>
              <Button
                variant="outlined"
                onClick={() => {
                  const updatedPersonalTargeting = [...personalTargeting];
                  updatedPersonalTargeting[index].urls.push("");
                  setPersonalTargeting(updatedPersonalTargeting);
                }}
              >
                Agregar URL
              </Button>
            </div>
          ))}
          <ThemeProvider theme={theme}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Button
                onClick={handleAddTracking}
                disabled={
                  !dataForm.previewUrl ||
                  dataForm.countriesTargeting?.length === 0
                }
              >
                Agregar Tracking
              </Button>
              {(!dataForm.previewUrl ||
                dataForm.countriesTargeting?.length === 0) && (
                <Typography
                  sx={{ color: "red", marginLeft: "0px", fontSize: "small" }}
                >
                  Para agregar un Personal Targeting, primero debes agregar un
                  General Targeting.
                </Typography>
              )}
            </Box>
          </ThemeProvider>
        </div>
      )}
    </Box>
  );
};

export default TrackingAndTargeting;
